import { StyledDefaultPrimaryButton, StyledForm } from 'styles';
import { Box, TextField, Typography } from '@mui/material';
import { Alert, AlertTitle } from '@mui/material';
import { useState, useRef, MouseEventHandler } from 'react';
import keyBy from 'lodash/keyBy';
import { booleanHash, booleanItemsArray, DataType, dataTypes } from '@constants';
import { SingleValuePickerV2 } from 'components/SingleValuePicker';
import useLocalDispatch from 'hooks/useLocalDispatch';
import { calculateAlertSeverityByStatusCode } from 'utils';
import JsonViewer from 'shared/JsonViewer';
import { integrationForceUpdate } from 'thunk';
import { convertBooleanStringToBoolean } from 'ReportsModule/utils';
import { useNotification } from 'hooks/useNotification';
import { useTenantRealmDropdownsFlow } from 'TenantModule/hooks/useTenantRealmDropdownsFlow';
import { TenantSelector } from 'TenantModule/components/TenantSelector';
import RealmSelector from 'SandboxModule/components/RealmSelector';

const stateId = 'force-update-card';

const defaultBooleanValue = booleanHash.false;
const integrationDataTypes = dataTypes;
const integrationDataTypesHash = keyBy(integrationDataTypes, 'value');
const defaultDataType = integrationDataTypesHash[DataType.TimeEntry];
const integrationDataTypesArray = integrationDataTypes.map((i) => i.value);

export const ForceUpdateForm = () => {
  const { dispatchLocal, selectLocalData } = useLocalDispatch();
  const { showSuccess, showError } = useNotification();
  const { isRequesting, data: stateData } = selectLocalData<{
    status: number;
    data: string;
  }>({
    stateId,
  });

  // Tenant/realm selection state (shared dropdowns)
  const { selectedTenant, selectedRealm, onSelectTenantRealm, onSelectRealm } =
    useTenantRealmDropdownsFlow();
  const realmDropdownRef = useRef<HTMLDivElement>(null);
  const onTenantSelectorClick: MouseEventHandler<HTMLElement> = (e) => {
    if (e.currentTarget && realmDropdownRef.current) {
      onSelectTenantRealm({
        tenantDropdownTarget: e.currentTarget,
        realmDropdownTarget: realmDropdownRef.current,
      });
    }
  };
  const onRealmSelectorClick: MouseEventHandler<HTMLElement> = (e) => {
    if (selectedTenant) {
      onSelectRealm({ target: e.currentTarget, tenantId: selectedTenant.id });
    }
  };

  const [forceUpdateParams, setForceUpdateParams] = useState<{
    mosaicTeamId: string;
    targetServiceId: string;
    ignoreWaitingResponse: string;
    dataType: string;
    targetSide: string;
  }>({
    dataType: defaultDataType.value,
    targetSide: defaultBooleanValue.value,
    mosaicTeamId: '',
    targetServiceId: '',
    ignoreWaitingResponse: defaultBooleanValue.value,
  });

  const onForceUpdateValueChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value, name } = event.target;
    if (name === 'dataType') {
      setForceUpdateParams({
        ...forceUpdateParams,
        [name]: integrationDataTypesHash[value].value,
      });
    } else if (name === 'targetSide' || name === 'ignoreWaitingResponse') {
      setForceUpdateParams({
        ...forceUpdateParams,
        [name]: booleanHash[value].value,
      });
    } else {
      setForceUpdateParams({
        ...forceUpdateParams,
        [name]: value,
      });
    }
  };

  const onForceUpdateClick: React.MouseEventHandler<HTMLFormElement> = (event) => {
    event.preventDefault();

    const { dataType, targetSide, mosaicTeamId, targetServiceId, ignoreWaitingResponse } =
      forceUpdateParams;

    if (targetServiceId.trim() && mosaicTeamId.trim()) {
      const params = {
        targetServiceId: Number(targetServiceId),
        targetSide: convertBooleanStringToBoolean(targetSide),
        mosaicTeamId: Number(mosaicTeamId),
        dataType,
        ignoreWaitingResponse: convertBooleanStringToBoolean(ignoreWaitingResponse),
        tenantName: selectedTenant?.tenantName || undefined,
        realmId: selectedRealm?.realm_id || undefined,
      };
      dispatchLocal(integrationForceUpdate, {
        params,
        stateId,
        formatSuccessResponse: ({ response }) => {
          return {
            status: response.status,
            data: response.data,
          };
        },
        formatFailureResponse: ({ error }: { error: any }) => {
          return {
            status: error.status,
            data: error.data,
          };
        },
        onSuccessCallback: ({ response }) => {
          const error = response.data?.errors?.[0];
          if (error) {
            showError(`Failed to trigger force update. ${error}`);
          } else {
            showSuccess('Successfully triggered force update.');
          }
        },
      });
    }
  };

  return (
    <>
      <Typography variant="h6" component="h3" sx={{ fontWeight: 600, fontSize: '1.125rem', mb: 1 }}>
        Update Backend from Integration Server
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Force Update
      </Typography>
      <StyledForm onSubmit={onForceUpdateClick}>
        <Box flex={1} display={'flex'} flexDirection={'column'} gap={4}>
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
              Organization
            </Typography>
            <Box display="grid" gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr' }} gap={2}>
              {selectedTenant ? (
                <TenantSelector onClick={onTenantSelectorClick} tenantId={selectedTenant.id} />
              ) : (
                <TenantSelector onClick={onTenantSelectorClick} tenantId={1} />
              )}
              <RealmSelector
                onClick={onRealmSelectorClick}
                realmId={selectedRealm?.realm_id || ''}
                ref={realmDropdownRef}
              />
            </Box>
          </Box>
          <TextField
            label="Mosaic Team Id"
            type="number"
            name="mosaicTeamId"
            value={forceUpdateParams.mosaicTeamId}
            onChange={onForceUpdateValueChange}
            required
          />
          <TextField
            label="Integration ID"
            type="number"
            name="targetServiceId"
            value={forceUpdateParams.targetServiceId}
            onChange={onForceUpdateValueChange}
            required
          />
          <SingleValuePickerV2
            items={integrationDataTypesArray}
            item={forceUpdateParams.dataType}
            itemsHash={integrationDataTypesHash}
            inputLabel="Data Type"
            inputName="dataType"
            onValueChange={onForceUpdateValueChange}
          />
          <SingleValuePickerV2
            items={booleanItemsArray}
            item={forceUpdateParams.targetSide}
            itemsHash={booleanHash}
            inputLabel="Target Side"
            inputName="targetSide"
            onValueChange={onForceUpdateValueChange}
          />
          <SingleValuePickerV2
            items={booleanItemsArray}
            item={forceUpdateParams.ignoreWaitingResponse}
            itemsHash={booleanHash}
            inputLabel="Ignore Waiting Response"
            inputName="ignoreWaitingResponse"
            onValueChange={onForceUpdateValueChange}
          />
        </Box>

        <Box
          display={'flex'}
          flexDirection={'column'}
          alignItems={'stretch'}
          gap={6}
          sx={{ mt: 'auto' }}
        >
          {!isRequesting && stateData && (
            <Alert severity={calculateAlertSeverityByStatusCode(stateData.status)}>
              <AlertTitle>{stateData.status}</AlertTitle>
              <JsonViewer collapsed src={{ response: stateData.data || '' }} />
            </Alert>
          )}
          <StyledDefaultPrimaryButton variant="contained" type="submit" disabled={isRequesting}>
            Request
          </StyledDefaultPrimaryButton>
        </Box>
      </StyledForm>
    </>
  );
};
